import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldAlert } from "lucide-react";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PlatformLogin() {
  const { login } = usePlatformAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Only ever back into the console — a tenant path here would be a session
  // mix-up, and the two sessions are deliberately separate.
  const nextRaw = params.get("next");
  const next = nextRaw && nextRaw.startsWith("/platform") ? nextRaw : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(form.email, form.password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signed in to the platform console");
      navigate(next || "/platform");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#14121f] px-4" data-testid="platform-login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl bg-violet-600/15 text-violet-400">
            <ShieldAlert size={22} />
          </div>
          <div className="font-heading text-2xl font-bold text-white">SyncBooks Platform</div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-violet-400">
            Superadmin Console
          </div>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="platform-login-form"
        >
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Email</Label>
            <Input
              data-testid="platform-email-input"
              type="email"
              autoComplete="username"
              value={form.email}
              onChange={set("email")}
              className="border-white/10 bg-white/5 text-white placeholder:text-slate-500"
              placeholder="superadmin@erp4u.in"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Password</Label>
            <Input
              data-testid="platform-password-input"
              type="password"
              autoComplete="current-password"
              value={form.password}
              onChange={set("password")}
              className="border-white/10 bg-white/5 text-white"
              required
            />
          </div>

          {error && (
            <div
              className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300"
              data-testid="platform-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="platform-login-submit"
            className="w-full gap-1.5 bg-violet-600 hover:bg-violet-700"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-[11px] text-slate-500">
          This console is separate from tenant sign-in. Business users should use{" "}
          <a href="/login" className="text-violet-400 hover:underline">
            /login
          </a>
          .
        </p>
      </div>
    </div>
  );
}
